import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import TrustWealthLogo from '../components/TrustWealthLogo';
import { validateOTP } from '../utils/otpValidation';
import { ShieldCheckIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const { useState } = React;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [tempUserCredential, setTempUserCredential] = useState(null);

  async function handleInitialSubmit(e) {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      const userCredential = await login(email, password);
      setTempUserCredential(userCredential);
      setShowOtpStep(true);
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      if (!validateOTP(otp)) throw new Error('Invalid OTP');
      const userDoc = await getDoc(doc(db, 'users', tempUserCredential.user.uid));
      const userData = userDoc.data();
      if (userData.role === 'admin') navigate('/admin');
      else navigate('/dashboard');
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(err.message === 'Invalid OTP' ? 'Invalid OTP. Please try again.' : 'Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left: branding panel */}
      <div className="relative flex-1 flex flex-col justify-between bg-[#0a1628] px-6 py-10 sm:px-10 lg:px-12 lg:py-14">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:48px_48px]" aria-hidden />
        <Link to="/" className="relative inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-medium transition-colors">
          <ArrowLeftIcon className="w-4 h-4" />
          Back to home
        </Link>
        <div className="relative flex flex-col justify-center min-h-[280px]">
          <div className="[&_img]:invert [&_img]:opacity-95 w-fit">
            <TrustWealthLogo />
          </div>
          <p className="mt-8 text-primary-400 text-sm font-semibold tracking-widest uppercase">
            Member sign in
          </p>
          <h1 className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white max-w-md">
            {showOtpStep ? 'Verify your identity' : 'Welcome back'}
          </h1>
          <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-sm">
            {showOtpStep
              ? 'Enter the 6-digit code from your authenticator app to continue.'
              : 'Sign in to manage your accounts, pay bills, and more.'}
          </p>
          <div className="mt-8 flex items-center gap-3 text-slate-500 text-sm">
            <ShieldCheckIcon className="w-5 h-5 text-primary-500 shrink-0" />
            <span>Secure login with two-factor authentication</span>
          </div>
        </div>
        <p className="relative text-slate-500 text-xs mt-6">
          © {new Date().getFullYear()} Willowvale Credit Union. NCUA insured.
        </p>
      </div>

      {/* Right: form panel */}
      <div className="flex-1 flex flex-col justify-center bg-white px-6 py-12 sm:px-10 lg:px-16 lg:py-14">
        <div className="w-full max-w-md mx-auto">
          <div className="lg:hidden mb-8">
            <TrustWealthLogo />
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" role="alert">
              {error}
            </div>
          )}

          {!showOtpStep ? (
            <form className="space-y-5" onSubmit={handleInitialSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="••••••••"
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-slate-600">Remember me</span>
                </label>
                <a href="#" className="text-primary-600 font-medium hover:text-primary-700">Forgot password?</a>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 rounded-xl font-semibold text-white bg-primary-500 hover:bg-primary-600 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Continue'}
              </button>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={handleOtpSubmit}>
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-slate-700 mb-1">One-time password (OTP)</label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  required
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="block w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-center tracking-[0.4em] font-mono text-lg"
                  placeholder="000000"
                />
                <p className="mt-1.5 text-sm text-slate-500">Enter the 6-digit code from your authenticator app.</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowOtpStep(false); setOtp(''); setError(''); }}
                  disabled={loading}
                  className="flex-1 py-3 px-4 rounded-xl font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-white bg-primary-500 hover:bg-primary-600 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify & sign in'}
                </button>
              </div>
            </form>
          )}

          <p className="mt-8 text-center text-sm text-slate-500">
            Don’t have an account?{' '}
            <Link to="/signup" className="font-medium text-primary-600 hover:text-primary-700">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
